import type { CalendarEvent } from '@/types';

const ENDPOINT = 'https://timevault-sync.baguedde29.workers.dev';
const LAST_SYNC_KEY = 'timevault_last_sync';

export async function pullEvents(): Promise<CalendarEvent[] | null> {
  try {
    const res = await fetch(ENDPOINT, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function pushEvents(events: CalendarEvent[]): Promise<boolean> {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(events),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function getLastSyncTime(): number {
  return parseInt(localStorage.getItem(LAST_SYNC_KEY) || '0', 10);
}

export function setLastSyncTime(time: number): void {
  localStorage.setItem(LAST_SYNC_KEY, String(time));
}

/**
 * Merge local and remote event lists.
 * - Remote events that are newer → override local.
 * - Local events created after lastSyncTime → new, keep them.
 * - Local events older than lastSyncTime that don't exist on remote → deleted remotely, remove them.
 */
export function mergeEvents(
  local: CalendarEvent[],
  remote: CalendarEvent[],
  lastSyncTime: number
): CalendarEvent[] {
  const merged = new Map<string, CalendarEvent>();

  // Start with remote
  for (const e of remote) merged.set(e.id, e);

  // Layer local on top
  for (const e of local) {
    if (merged.has(e.id)) {
      // Both have it — keep newer
      if (e.updatedAt > merged.get(e.id)!.updatedAt) {
        merged.set(e.id, e);
      }
    } else {
      // Only local — keep if created after last sync (it's new on this device)
      if (e.createdAt > lastSyncTime) {
        merged.set(e.id, e);
      }
      // else: was deleted from another device → omit
    }
  }

  return Array.from(merged.values());
}
